// Science — master index. Structure follows the Grade9Ready PDF's
// 13 sections, grouped into four disciplines:
//   • Biology         — PDF sections 1–4
//   • Chemistry       — PDF sections 5–7
//   • Physics         — PDF sections 8–12
//   • Combined        — cross-discipline / whole-subject material (PDF section 13)
//
// Each section carries a `pdfSection` number so we can always cross-reference
// back to the book. New sections are scaffolded with `status: 'coming_soon'`
// and populated progressively as we author rich content.

import { cellBiologyTopics } from './biologySection1';
import { humansAsOrganismsTopics } from './biologySection2';
import { plantsAndEcosystemsTopics } from './biologySection3';
import { inheritanceVariationSurvivalTopics } from './biologySection4';
import { classifyingMaterialsTopics } from './chemistrySection5';
import { chemicalChangesTopics } from './chemistrySection6';
import { earthAndAtmosphereTopics } from './chemistrySection7';
import { energyAndMatterTopics } from './physicsSection8';
import { forcesAndMotionTopics } from './physicsSection9';
import { wavesTopics } from './physicsSection10';
import { electricityAndMagnetismTopics } from './physicsSection11';
import { earthAndBeyondTopics } from './physicsSection12';
import {
  b1CellBiologyTopics,
  b2OrganisationTopics,
  b3InfectionResponseTopics,
  b4BioenergeticsTopics,
  b5HomeostasisTopics,
  b6InheritanceTopics,
  b7EcologyTopics,
} from './biologyGCSE';
import {
  c1AtomicStructureTopics,
  c2BondingTopics,
  c3QuantitativeTopics,
  c4ChemicalChangesTopics,
  c5EnergyChangesTopics,
  c6RateExtentTopics,
  c7OrganicTopics,
  c8AnalysisTopics,
  c9AtmosphereTopics,
  c10UsingResourcesTopics,
} from './chemistryGCSE';
import {
  p1EnergyTopics,
  p2ElectricityTopics,
  p3ParticleModelTopics,
  p4AtomicStructureTopics as p4AtomicTopics,
  p5ForcesTopics,
  p6WavesTopics,
  p7MagnetismTopics,
} from './physicsGCSE';
import { attachExaminerInsights } from './examinerBank';

// Decorate every GCSE topic list with examiner-brain content from the central bank
// (no-op for KS3 sections — those don't appear in this map).
const _b1 = attachExaminerInsights(b1CellBiologyTopics);
const _b2 = attachExaminerInsights(b2OrganisationTopics);
const _b3 = attachExaminerInsights(b3InfectionResponseTopics);
const _b4 = attachExaminerInsights(b4BioenergeticsTopics);
const _b5 = attachExaminerInsights(b5HomeostasisTopics);
const _b6 = attachExaminerInsights(b6InheritanceTopics);
const _b7 = attachExaminerInsights(b7EcologyTopics);
const _c1 = attachExaminerInsights(c1AtomicStructureTopics);
const _c2 = attachExaminerInsights(c2BondingTopics);
const _c3 = attachExaminerInsights(c3QuantitativeTopics);
const _c4 = attachExaminerInsights(c4ChemicalChangesTopics);
const _c5 = attachExaminerInsights(c5EnergyChangesTopics);
const _c6 = attachExaminerInsights(c6RateExtentTopics);
const _c7 = attachExaminerInsights(c7OrganicTopics);
const _c8 = attachExaminerInsights(c8AnalysisTopics);
const _c9 = attachExaminerInsights(c9AtmosphereTopics);
const _c10 = attachExaminerInsights(c10UsingResourcesTopics);
const _p1 = attachExaminerInsights(p1EnergyTopics);
const _p2 = attachExaminerInsights(p2ElectricityTopics);
const _p3 = attachExaminerInsights(p3ParticleModelTopics);
const _p4 = attachExaminerInsights(p4AtomicTopics);
const _p5 = attachExaminerInsights(p5ForcesTopics);
const _p6 = attachExaminerInsights(p6WavesTopics);
const _p7 = attachExaminerInsights(p7MagnetismTopics);

// ---------- Biology (PDF 1–4) ----------
export const biologySections = [
  {
    id: 1,
    pdfSection: 1,
    title: 'Cells and Respiration',
    slug: 'cells-and-respiration',
    color: 'emerald',
    icon: 'Leaf',
    description: 'Microscopes, animal & plant cells, cell organisation, diffusion and respiration.',
    topics: cellBiologyTopics,
    status: 'live',
  },
  {
    id: 2,
    pdfSection: 2,
    title: 'Humans as Organisms',
    slug: 'humans-as-organisms',
    color: 'teal',
    icon: 'Person',
    description: 'Nutrition, digestion, skeleton, muscles, breathing, reproduction, health.',
    topics: humansAsOrganismsTopics,
    status: 'live', // Digestion is authored; other topics are scaffolds.
  },
  {
    id: 3,
    pdfSection: 3,
    title: 'Plants and Ecosystems',
    slug: 'plants-and-ecosystems',
    color: 'lime',
    icon: 'Plant',
    description: 'Plant nutrition, reproduction, seed dispersal, food chains and webs.',
    topics: plantsAndEcosystemsTopics,
    status: 'live',
  },
  {
    id: 4,
    pdfSection: 4,
    title: 'Inheritance, Variation and Survival',
    slug: 'inheritance-variation-survival',
    color: 'emerald',
    icon: 'DnaDouble',
    description: 'DNA, variation, natural selection, extinct and preserved species.',
    topics: inheritanceVariationSurvivalTopics,
    status: 'live',
  },
  // ---------- KS4 GCSE Biology (B1-B7) ----------
  {
    id: 'b1',
    pdfSection: 'B1',
    keyStage: 'ks4',
    examTier: 'gcse',
    title: 'GCSE B1 — Cell Biology',
    slug: 'gcse-cell-biology',
    color: 'emerald',
    icon: 'Atom',
    description: 'Eukaryotes, prokaryotes, microscopy, stem cells, mitosis, transport (RP1, RP2).',
    topics: _b1,
    status: 'live',
  },
  {
    id: 'b2',
    pdfSection: 'B2',
    keyStage: 'ks4',
    examTier: 'gcse',
    title: 'GCSE B2 — Organisation',
    slug: 'gcse-organisation',
    color: 'teal',
    icon: 'Heart',
    description: 'Cells → tissues → organs, enzymes, food tests (RP3), heart, blood, CHD, cancer.',
    topics: _b2,
    status: 'live',
  },
  {
    id: 'b3',
    pdfSection: 'B3',
    keyStage: 'ks4',
    examTier: 'gcse',
    title: 'GCSE B3 — Infection & Response',
    slug: 'gcse-infection-response',
    color: 'rose',
    icon: 'Virus',
    description: 'Pathogens, bacterial/viral/fungal diseases, immune system, vaccination, drug development, monoclonal antibodies.',
    topics: _b3,
    status: 'live',
  },
  {
    id: 'b4',
    pdfSection: 'B4',
    keyStage: 'ks4',
    examTier: 'gcse',
    title: 'GCSE B4 — Bioenergetics',
    slug: 'gcse-bioenergetics',
    color: 'lime',
    icon: 'Plant',
    description: 'Photosynthesis, limiting factors (RP4), uses of glucose, aerobic + anaerobic respiration.',
    topics: _b4,
    status: 'live',
  },
  {
    id: 'b5',
    pdfSection: 'B5',
    keyStage: 'ks4',
    examTier: 'gcse',
    title: 'GCSE B5 — Homeostasis & Response',
    slug: 'gcse-homeostasis-response',
    color: 'cyan',
    icon: 'Brain',
    description: 'Nervous system, reflexes (RP5), brain, eye, hormones, blood glucose, reproduction.',
    topics: _b5,
    status: 'live',
  },
  {
    id: 'b6',
    pdfSection: 'B6',
    keyStage: 'ks4',
    examTier: 'gcse',
    title: 'GCSE B6 — Inheritance, Variation & Evolution',
    slug: 'gcse-inheritance-variation-evolution',
    color: 'emerald',
    icon: 'DnaDouble',
    description: 'Meiosis, DNA, Punnett squares, sex determination, mutation, natural selection, GM, classification.',
    topics: _b6,
    status: 'live',
  },
  {
    id: 'b7',
    pdfSection: 'B7',
    keyStage: 'ks4',
    examTier: 'gcse',
    title: 'GCSE B7 — Ecology',
    slug: 'gcse-ecology',
    color: 'green',
    icon: 'Leaf',
    description: 'Biotic/abiotic factors, adaptations, sampling (RP6), food chains, cycles, biodiversity, food security.',
    topics: _b7,
    status: 'live',
  },
];

// ---------- Chemistry (PDF 5–7) ----------
export const chemistrySections = [
  {
    id: 5,
    pdfSection: 5,
    title: 'Classifying Materials',
    slug: 'classifying-materials',
    color: 'violet',
    icon: 'Cube',
    description: 'States of matter, particle theory, metals vs non-metals, separating mixtures.',
    topics: classifyingMaterialsTopics,
    status: 'live',
  },
  {
    id: 6,
    pdfSection: 6,
    title: 'Chemical Changes',
    slug: 'chemical-changes',
    color: 'indigo',
    icon: 'FlaskRound',
    description: 'Chemical reactions, equations, acids, alkalis, neutralisation, reactivity series.',
    topics: chemicalChangesTopics,
    status: 'live',
  },
  {
    id: 7,
    pdfSection: 7,
    title: 'The Earth and the Atmosphere',
    slug: 'earth-and-atmosphere',
    color: 'purple',
    icon: 'Globe',
    description: 'Earth\u2019s structure, rock types, the rock and carbon cycles, climate.',
    topics: earthAndAtmosphereTopics,
    status: 'live',
  },
  // ---------- KS4 GCSE Chemistry (C1-C10) ----------
  {
    id: 'c1',
    pdfSection: 'C1',
    keyStage: 'ks4',
    examTier: 'gcse',
    title: 'GCSE C1 — Atomic Structure & Periodic Table',
    slug: 'gcse-atomic-structure',
    color: 'sky',
    icon: 'Atom',
    description: 'Atoms, isotopes, electronic configuration, Mendeleev, Groups 1, 7 and 0.',
    topics: c1AtomicStructureTopics,
    status: 'live',
  },
  {
    id: 'c2',
    pdfSection: 'C2',
    keyStage: 'ks4',
    examTier: 'gcse',
    title: 'GCSE C2 — Bonding, Structure & Properties',
    slug: 'gcse-bonding-structure',
    color: 'indigo',
    icon: 'Atom',
    description: 'Ionic, covalent, metallic; states of matter; giant covalent; nanoparticles; polymers.',
    topics: c2BondingTopics,
    status: 'live',
  },
  {
    id: 'c3',
    pdfSection: 'C3',
    keyStage: 'ks4',
    examTier: 'gcse',
    title: 'GCSE C3 — Quantitative Chemistry',
    slug: 'gcse-quantitative-chemistry',
    color: 'cyan',
    icon: 'Calculator',
    description: 'Conservation of mass, moles, %yield, atom economy, concentration calculations.',
    topics: c3QuantitativeTopics,
    status: 'live',
  },
  {
    id: 'c4',
    pdfSection: 'C4',
    keyStage: 'ks4',
    examTier: 'gcse',
    title: 'GCSE C4 — Chemical Changes',
    slug: 'gcse-chemical-changes',
    color: 'rose',
    icon: 'Flask',
    description: 'Reactivity series, acids, alkalis, neutralisation (RP), titration, electrolysis.',
    topics: c4ChemicalChangesTopics,
    status: 'live',
  },
  {
    id: 'c5',
    pdfSection: 'C5',
    keyStage: 'ks4',
    examTier: 'gcse',
    title: 'GCSE C5 — Energy Changes',
    slug: 'gcse-energy-changes',
    color: 'orange',
    icon: 'Fire',
    description: 'Exothermic / endothermic reactions, reaction profiles, bond-energy calculations.',
    topics: c5EnergyChangesTopics,
    status: 'live',
  },
  {
    id: 'c6',
    pdfSection: 'C6',
    keyStage: 'ks4',
    examTier: 'gcse',
    title: 'GCSE C6 — Rate & Extent of Chemical Change',
    slug: 'gcse-rate-extent',
    color: 'fuchsia',
    icon: 'TrendingUp',
    description: 'Rate of reaction, collision theory, catalysts, reversible reactions, Le Chatelier.',
    topics: c6RateExtentTopics,
    status: 'live',
  },
  {
    id: 'c7',
    pdfSection: 'C7',
    keyStage: 'ks4',
    examTier: 'gcse',
    title: 'GCSE C7 — Organic Chemistry',
    slug: 'gcse-organic-chemistry',
    color: 'emerald',
    icon: 'Atom',
    description: 'Crude oil, fractional distillation, alkanes/alkenes, cracking, alcohols, polymers.',
    topics: c7OrganicTopics,
    status: 'live',
  },
  {
    id: 'c8',
    pdfSection: 'C8',
    keyStage: 'ks4',
    examTier: 'gcse',
    title: 'GCSE C8 — Chemical Analysis',
    slug: 'gcse-chemical-analysis',
    color: 'teal',
    icon: 'MagnifyingGlass',
    description: 'Pure substances, formulations, paper chromatography (RP), Rf values.',
    topics: c8AnalysisTopics,
    status: 'live',
  },
  {
    id: 'c9',
    pdfSection: 'C9',
    keyStage: 'ks4',
    examTier: 'gcse',
    title: 'GCSE C9 — Chemistry of the Atmosphere',
    slug: 'gcse-atmosphere',
    color: 'sky',
    icon: 'Globe',
    description: 'Atmosphere composition + evolution, greenhouse gases, climate change, pollutants.',
    topics: c9AtmosphereTopics,
    status: 'live',
  },
  {
    id: 'c10',
    pdfSection: 'C10',
    keyStage: 'ks4',
    examTier: 'gcse',
    title: 'GCSE C10 — Using Resources',
    slug: 'gcse-using-resources',
    color: 'amber',
    icon: 'Recycle',
    description: 'Finite vs renewable, LCAs, potable water + waste treatment, Haber process.',
    topics: c10UsingResourcesTopics,
    status: 'live',
  },
];

// ---------- Physics (PDF 8–12) ----------
export const physicsSections = [
  {
    id: 8,
    pdfSection: 8,
    title: 'Energy and Matter',
    slug: 'energy-and-matter',
    color: 'amber',
    icon: 'Lightning',
    description: 'Energy stores, transfers, conservation, work done, energy resources.',
    topics: energyAndMatterTopics,
    status: 'live',
  },
  {
    id: 9,
    pdfSection: 9,
    title: 'Forces and Motion',
    slug: 'forces-and-motion',
    color: 'orange',
    icon: 'ArrowsOutCardinal',
    description: 'Speed, relative motion, forces, friction, air and water resistance.',
    topics: forcesAndMotionTopics,
    status: 'live',
  },
  {
    id: 10,
    pdfSection: 10,
    title: 'Waves',
    slug: 'waves',
    color: 'yellow',
    icon: 'Waveform',
    description: 'Water, light and sound waves; reflection, refraction, colour, hearing.',
    topics: wavesTopics,
    status: 'live',
  },
  {
    id: 11,
    pdfSection: 11,
    title: 'Electricity and Magnetism',
    slug: 'electricity-and-magnetism',
    color: 'amber',
    icon: 'Plug',
    description: 'Circuits, current, potential difference, static, magnets, electromagnets.',
    topics: electricityAndMagnetismTopics,
    status: 'live',
  },
  {
    id: 12,
    pdfSection: 12,
    title: 'The Earth and Beyond',
    slug: 'earth-and-beyond',
    color: 'orange',
    icon: 'Planet',
    description: 'Gravity, the Sun and stars, the solar system, day/night and the seasons.',
    topics: earthAndBeyondTopics,
    status: 'live',
  },
  // ---------- KS4 GCSE Physics (P1-P7) ----------
  {
    id: 'p1',
    pdfSection: 'P1',
    keyStage: 'ks4',
    examTier: 'gcse',
    title: 'GCSE P1 — Energy',
    slug: 'gcse-energy',
    color: 'amber',
    icon: 'Lightning',
    description: 'Energy stores & transfers, KE/GPE/EPE calculations, SHC (RP1), power, efficiency, energy resources.',
    topics: p1EnergyTopics,
    status: 'live',
  },
  {
    id: 'p2',
    pdfSection: 'P2',
    keyStage: 'ks4',
    examTier: 'gcse',
    title: 'GCSE P2 — Electricity',
    slug: 'gcse-electricity',
    color: 'yellow',
    icon: 'Lightning',
    description: 'Current, p.d. resistance, Ohm\u2019s law, I-V characteristics (RP2), mains, electrical power.',
    topics: p2ElectricityTopics,
    status: 'live',
  },
  {
    id: 'p3',
    pdfSection: 'P3',
    keyStage: 'ks4',
    examTier: 'gcse',
    title: 'GCSE P3 — Particle Model of Matter',
    slug: 'gcse-particle-model',
    color: 'blue',
    icon: 'Atom',
    description: 'Density (RP3), changes of state, internal energy, latent heat, gas pressure, Boyle\u2019s law.',
    topics: p3ParticleModelTopics,
    status: 'live',
  },
  {
    id: 'p4',
    pdfSection: 'P4',
    keyStage: 'ks4',
    examTier: 'gcse',
    title: 'GCSE P4 — Atomic Structure',
    slug: 'gcse-physics-atomic-structure',
    color: 'purple',
    icon: 'Atom',
    description: 'Atomic models, isotopes, alpha/beta/gamma decay, nuclear equations, half-life.',
    topics: p4AtomicTopics,
    status: 'live',
  },
  {
    id: 'p5',
    pdfSection: 'P5',
    keyStage: 'ks4',
    examTier: 'gcse',
    title: 'GCSE P5 — Forces',
    slug: 'gcse-forces',
    color: 'red',
    icon: 'Crosshair',
    description: 'Vectors, Newton\u2019s laws, kinematics, stopping distance, Hooke\u2019s law (RP4).',
    topics: p5ForcesTopics,
    status: 'live',
  },
  {
    id: 'p6',
    pdfSection: 'P6',
    keyStage: 'ks4',
    examTier: 'gcse',
    title: 'GCSE P6 — Waves',
    slug: 'gcse-waves',
    color: 'cyan',
    icon: 'WaveSine',
    description: 'Transverse vs longitudinal, EM spectrum, refraction & lenses (RP5).',
    topics: p6WavesTopics,
    status: 'live',
  },
  {
    id: 'p7',
    pdfSection: 'P7',
    keyStage: 'ks4',
    examTier: 'gcse',
    title: 'GCSE P7 — Magnetism & Electromagnetism',
    slug: 'gcse-magnetism',
    color: 'indigo',
    icon: 'Magnet',
    description: 'Magnets, fields, electromagnets, motor effect (Higher), transformers (Higher).',
    topics: p7MagnetismTopics,
    status: 'live',
  },
];

// ---------- Combined (PDF 13 + cross-discipline) ----------
export const combinedSections = [
  {
    id: 13,
    pdfSection: 13,
    title: 'Exam Practice',
    slug: 'exam-practice',
    color: 'cyan',
    icon: 'Exam',
    description: 'Mixed practice tests and a full KS3 Science practice exam.',
    topics: [],
    status: 'live',
  },
];

// ---------- Disciplines ----------
export const scienceDisciplines = [
  {
    id: 'biology',
    title: 'Biology',
    slug: 'biology',
    color: 'emerald',
    icon: 'Leaf',
    description: 'How living things work — cells, humans, plants, inheritance and survival.',
    sections: biologySections,
  },
  {
    id: 'chemistry',
    title: 'Chemistry',
    slug: 'chemistry',
    color: 'violet',
    icon: 'FlaskRound',
    description: 'Materials, chemical reactions, acids, and the chemistry of our planet.',
    sections: chemistrySections,
  },
  {
    id: 'physics',
    title: 'Physics',
    slug: 'physics',
    color: 'amber',
    icon: 'Lightning',
    description: 'Energy, forces, waves, electricity and the wider universe.',
    sections: physicsSections,
  },
  {
    id: 'combined',
    title: 'Combined',
    slug: 'combined',
    color: 'cyan',
    icon: 'GraduationCap',
    description: 'Cross-discipline material — mixed practice tests and full exam papers.',
    sections: combinedSections,
  },
];

// Helper for the ScienceHub counter
export function getScienceStats() {
  let liveSections = 0;
  let liveTopics = 0;
  let totalSections = 0;
  for (const d of scienceDisciplines) {
    for (const s of d.sections) {
      totalSections += 1;
      if (s.status === 'live') {
        liveSections += 1;
        // Only count authored (non-scaffold) topics: scaffolds lack learn/practice.
        liveTopics += (s.topics || []).filter((t) => t?.learn || t?.practice).length;
      }
    }
  }
  return { liveSections, liveTopics, totalSections };
}
